import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { invalidateContractFinancing } from './financing/hooks'
import {
  cancelContract,
  changeSalesPerson,
  completeStage,
  createContract,
  getContract,
  getFormSchema,
  getConfigurationFormSchema,
  listContracts,
  overrideStage,
  updateSection,
} from './api'
import type {
  CancelContractRequest,
  Contract,
  CreateContractRequest,
  OverrideStageRequest,
  UpdateSectionRequest,
} from './types'

export const contractKeys = {
  all: ['contracts'] as const,
  list: (stage?: string) => ['contracts', 'list', stage ?? 'all'] as const,
  detail: (id: number) => ['contracts', 'detail', id] as const,
  formSchema: (objectType: string) => ['formSchema', objectType] as const,
}

export function useContracts(stage?: string) {
  return useQuery({
    queryKey: contractKeys.list(stage),
    queryFn: () => listContracts(stage),
  })
}

export function useContract(id: number | null | undefined) {
  return useQuery({
    queryKey: contractKeys.detail(id ?? -1),
    queryFn: () => getContract(id as number),
    enabled: typeof id === 'number' && id > 0,
  })
}

export function useContractFormSchema(configurationCode?: string | null) {
  return useQuery({
    queryKey: contractKeys.formSchema(configurationCode ?? 'contract'),
    queryFn: () => configurationCode ? getConfigurationFormSchema(configurationCode) : getFormSchema('contract'),
    enabled: configurationCode !== null,   // null = wait for contract to load; undefined = legacy fallback
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateSection(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ sectionCode, payload }: { sectionCode: string; payload: UpdateSectionRequest }) =>
      updateSection(contractId, sectionCode, payload),
    onMutate: async ({ payload }) => {
      await qc.cancelQueries({ queryKey: contractKeys.detail(contractId) })
      const prev = qc.getQueryData<Contract>(contractKeys.detail(contractId))
      if (prev) {
        qc.setQueryData<Contract>(contractKeys.detail(contractId), {
          ...prev,
          extras: { ...prev.extras, ...payload.extras },
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(contractKeys.detail(contractId), ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: contractKeys.detail(contractId) })
      // Lista pokazuje dane które mogły się zmienić (np. totalAmount/currency
      // synchronizowane z extras przez semantic_role) - refresh aby nie pokazywać
      // przeterminowanych wartości po powrocie do listy.
      qc.invalidateQueries({ queryKey: contractKeys.all })
      // total_amount wplywa na podsumowanie finansowania -> odswiez te zapytania
      invalidateContractFinancing(qc, contractId)
    },
  })
}

function invalidateStageDependentQueries(qc: ReturnType<typeof useQueryClient>, contractId: number) {
  // Stage change cascades into many caches: requirements gate, status visibility per
  // stage, contract detail (sectionVisibility + nextStage), plus list (currentStageCode
  // column changes).
  qc.invalidateQueries({ queryKey: contractKeys.detail(contractId) })
  qc.invalidateQueries({ queryKey: ['gating'] })
  qc.invalidateQueries({ queryKey: ['statuses'] })
  qc.invalidateQueries({ queryKey: contractKeys.all })
}

export function useCompleteStage(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => completeStage(contractId),
    onSuccess: () => invalidateStageDependentQueries(qc, contractId),
  })
}

export function useOverrideStage(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: OverrideStageRequest) => overrideStage(contractId, payload),
    onSuccess: () => invalidateStageDependentQueries(qc, contractId),
  })
}

export function useCancelContract(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CancelContractRequest) => cancelContract(contractId, payload),
    onSuccess: () => invalidateStageDependentQueries(qc, contractId),
  })
}

export function useCreateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateContractRequest) => createContract(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.all })
    },
  })
}

export function useChangeContractSalesPerson(contractId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (salesPersonId: number | null) => changeSalesPerson(contractId, salesPersonId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contractKeys.detail(contractId) })
      qc.invalidateQueries({ queryKey: contractKeys.all })
    },
  })
}

export function useDebouncedSectionUpdate(contractId: number, sectionCode: string, delayMs = 1500) {
  const mutation = useUpdateSection(contractId)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const buffer = useRef<Record<string, unknown>>({})

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const queue = (changes: Record<string, unknown>) => {
    buffer.current = { ...buffer.current, ...changes }
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const payload = { extras: buffer.current }
      buffer.current = {}
      mutation.mutate({ sectionCode, payload })
    }, delayMs)
  }

  return { queue, status: mutation.status, error: mutation.error }
}
